import React, { FormEventHandler, useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { RpcError } from 'grpc-web'
import { ListControlsRequest } from '@trustero/trustero-api-web/lib/model/control_pb'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { useControls } from 'src/components/async/model/control/useControls'
import { FrameworksAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { useCustomFrameworkWizard } from 'src/pages/Frameworks/context/CustomFrameworkWizardContext'
import { useCreateCustomFrameworkWithSections } from 'src/pages/Frameworks/frameworks.hooks'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import { ERROR_MESSAGES } from 'src/Utils/globalEnums'
import { FlexAlign, FlexColumn } from 'src/components/Reusable/Flex'
import { StyledTextInput } from 'src/components/Reusable/Inputs/TextInput/styles'
import { NewMarkdownEditor } from 'src/components/Reusable/Inputs/NewMarkdownEditor'
import { RadioInput } from 'src/components/Reusable/Inputs/Radio'
import { SelectItem } from 'src/components/Reusable/SelectDropdown/SelectDropdown.constants'
import { FrameworkSectionsEditor } from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor'
import { CompactMarkdown } from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.styles'
import {
  createEmptySectionDraft,
  getEmptyNameErrors,
} from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.helpers'
import { FrameworkSectionsEditorErrors } from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.types'
import {
  FrameworkSectionsCSVForm,
  csvRowsToSectionDrafts,
} from 'src/components/ModalForms/Frameworks/FrameworkSectionsCSVForm'
import { useConfirmationModal } from '../../useConfirmationModal'
import { ModalForm, ModalFormId } from '../..'
import { CsvV2ModalSubtitle } from '../../Reusable/UploadCSVModalV2.styles'
import { ImportFrameworkSectionsCSVRow } from './FrameworkSectionsCSV.constants'
import { parseDuplicateRequirementNameError } from './FrameworkSectionsCSV.helpers'
import {
  StyledComplianceFrameworkInputContainer,
  StyledFieldLabel,
} from './ConfirmFrameworkModal.styles'
import { ModeToggleRow } from './AddCustomComplianceFrameworkModal.styles'

const FRAMEWORK_NAME_MAX = 50
const FRAMEWORK_DESCRIPTION_MAX = 1000

type EntryMode = 'manual' | 'csv' | null

export const AddCustomComplianceFrameworkModal = (): JSX.Element => {
  const show = useIsShowModal(ModalFormId.ADD_CUSTOM_COMPLIANCE_FRAMEWORK)
  const hide = useHideModal({
    modalId: ModalFormId.ADD_CUSTOM_COMPLIANCE_FRAMEWORK,
  })
  const navigate = useNavigate()
  const { pageContext } = useParams()
  const submitWithSections = useCreateCustomFrameworkWithSections()

  const {
    name,
    setName,
    description,
    setDescription,
    sections,
    setSections,
    reset,
  } = useCustomFrameworkWizard()

  const [mode, setMode] = useState<EntryMode>(null)
  const [csvItems, setCsvItems] = useState<ImportFrameworkSectionsCSVRow[]>([])
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvErrors, setCsvErrors] = useState<string[]>([])
  const [showAttachmentError, setShowAttachmentError] = useState(false)
  const [nameError, setNameError] = useState('')
  const [manualErrors, setManualErrors] =
    useState<FrameworkSectionsEditorErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { data: controlsData, isLoading: controlsLoading } = useControls(
    new ListControlsRequest(),
    show,
  )

  const validControlIdSet = useMemo(
    () =>
      new Set((controlsData?.getItemsList() ?? []).map((c) => c.getModelId())),
    [controlsData],
  )

  const controlOptions = useMemo<SelectItem[]>(
    () =>
      (controlsData?.getItemsList() ?? []).map((c) => ({
        name: `${c.getModelId()} ${c.getName()}`,
        value: c.getModelId(),
      })),
    [controlsData],
  )

  const manualHasData = useMemo(
    () =>
      sections.some(
        (s) =>
          s.name.trim().length > 0 ||
          s.requirements.some((r) => r.name.trim().length > 0),
      ),
    [sections],
  )
  const csvHasData = !!csvFile || csvItems.length > 0

  const doSwitchToManual = useCallback(() => {
    setCsvFile(null)
    setCsvItems([])
    setCsvErrors([])
    setShowAttachmentError(false)
    setManualErrors({})
    setMode('manual')
    setSections((prev) =>
      prev.length > 0 ? prev : [createEmptySectionDraft()],
    )
  }, [setSections])

  const doSwitchToCSV = useCallback(() => {
    setSections([])
    setManualErrors({})
    setMode('csv')
  }, [setSections])

  const confirmSwitchToManual = useConfirmationModal({
    title: 'Switch to manual entry?',
    body: <p>This will clear the CSV you uploaded. Continue?</p>,
    confirmText: 'Switch',
    onConfirmCB: doSwitchToManual,
  })
  const confirmSwitchToCSV = useConfirmationModal({
    title: 'Switch to CSV upload?',
    body: (
      <p>
        This will clear the sections and requirements you&apos;ve entered.
        Continue?
      </p>
    ),
    confirmText: 'Switch',
    onConfirmCB: doSwitchToCSV,
  })

  const onSwitchToManual = () => {
    if (mode === 'manual') return
    if (csvHasData) confirmSwitchToManual()
    else doSwitchToManual()
  }
  const onSwitchToCSV = () => {
    if (mode === 'csv') return
    if (manualHasData) confirmSwitchToCSV()
    else doSwitchToCSV()
  }

  const closeAndReset = () => {
    reset()
    setMode(null)
    setCsvFile(null)
    setCsvItems([])
    setCsvErrors([])
    setShowAttachmentError(false)
    setNameError('')
    setManualErrors({})
    hide()
  }

  const sectionsForSubmit = useMemo(() => {
    if (mode === 'manual') return sections
    if (mode === 'csv') return csvRowsToSectionDrafts(csvItems)
    return []
  }, [mode, sections, csvItems])

  const disableSubmit =
    isSubmitting ||
    controlsLoading ||
    (mode === 'csv' && (csvErrors.length > 0 || !csvFile))

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    if (!name.trim()) {
      setNameError(ERROR_MESSAGES.EMPTY)
      return
    }
    setNameError('')
    if (mode === 'manual') {
      const { sectionNames, requirementNames } = getEmptyNameErrors(sections)
      if (
        Object.keys(sectionNames).length ||
        Object.keys(requirementNames).length
      ) {
        setManualErrors({ sectionNames, requirementNames })
        return
      }
      setManualErrors({})
    }
    if (mode === 'csv' && !csvFile) {
      setShowAttachmentError(true)
      return
    }
    if (mode === 'csv' && csvErrors.length > 0) return
    setIsSubmitting(true)
    try {
      const { complianceFrameworkRecordId } = await submitWithSections({
        name: name.trim(),
        description,
        sections: sectionsForSubmit,
      })
      closeAndReset()
      navigate(
        `/${pageContext}/${FrameworksAbsoluteRoutes.SHOW}/${complianceFrameworkRecordId}`,
      )
    } catch (err) {
      const rpcErr = err as RpcError
      const dupInfo = parseDuplicateRequirementNameError(rpcErr?.message ?? '')
      if (dupInfo) {
        const message = dupInfo.isExisting
          ? `A requirement named "${dupInfo.name}" already exists in this framework.`
          : `Duplicate requirement name "${dupInfo.name}".`
        if (mode === 'csv') {
          setCsvErrors([message])
        } else {
          toast.error(message)
        }
      } else {
        const errorMessage = getErrorMessage(rpcErr)
        toast.error(errorMessage.message)
        if (errorMessage.shouldLog) {
          log.error('AddCustomComplianceFrameworkModal submit failed', err)
        }
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalForm
      show={show}
      hide={closeAndReset}
      formId={ModalFormId.ADD_CUSTOM_COMPLIANCE_FRAMEWORK}
      size="xl"
      title="Add Custom Framework"
      submitText="Add"
      showOverflow
      disableSubmitButton={disableSubmit}
    >
      <form
        id={ModalFormId.ADD_CUSTOM_COMPLIANCE_FRAMEWORK}
        onSubmit={onSubmit}
      >
        <CsvV2ModalSubtitle>
          Create a custom compliance framework. Enter the framework details,
          then add sections and requirements manually or via CSV.
        </CsvV2ModalSubtitle>
        <FlexColumn gap={8} align={FlexAlign.FLEX_START} isFullWidth>
          <StyledComplianceFrameworkInputContainer>
            <p>Name</p>
            <StyledTextInput
              value={name}
              showError={!!nameError}
              maxLength={FRAMEWORK_NAME_MAX}
              placeholder="Enter framework name"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (nameError) setNameError('')
                setName(e.target.value)
              }}
            />
            <StyledFieldLabel
              validateForm={!!nameError}
              isInvalid={!!nameError}
              message={nameError}
            />
          </StyledComplianceFrameworkInputContainer>
          <StyledComplianceFrameworkInputContainer>
            <p>Details (optional)</p>
            <CompactMarkdown>
              <NewMarkdownEditor
                value={description}
                onChange={(v) =>
                  setDescription(v.slice(0, FRAMEWORK_DESCRIPTION_MAX))
                }
                placeholder="Add framework details and purpose"
              />
            </CompactMarkdown>
          </StyledComplianceFrameworkInputContainer>
          <ModeToggleRow>
            <RadioInput
              id="framework-mode-manual"
              name="framework-section-mode"
              label="Add sections manually"
              checked={mode === 'manual'}
              onChange={onSwitchToManual}
            />
            <RadioInput
              id="framework-mode-csv"
              name="framework-section-mode"
              label="Upload sections via CSV"
              checked={mode === 'csv'}
              onChange={onSwitchToCSV}
            />
          </ModeToggleRow>
          {mode === 'manual' && (
            <FrameworkSectionsEditor
              sections={sections}
              setSections={setSections}
              setErrors={setManualErrors}
              controlOptions={controlOptions}
              errors={manualErrors}
            />
          )}
          {mode === 'csv' && (
            <FrameworkSectionsCSVForm
              existingRequirementNames={[]}
              validControlIdSet={validControlIdSet}
              isSubmitting={isSubmitting}
              isLoadingControls={controlsLoading}
              showAttachmentError={showAttachmentError}
              onParseChange={(p) => {
                setCsvItems(p.items)
                setCsvFile(p.file)
                setCsvErrors(p.errors)
                if (p.file) setShowAttachmentError(false)
              }}
            />
          )}
        </FlexColumn>
      </form>
    </ModalForm>
  )
}
